import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import { useData } from '../context/DataContext';
import { useTheme } from '../theme/ThemeContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

const DashboardCharts = () => {
  const { kpi } = useData();
  const { theme } = useTheme();

  if (!kpi || !kpi.caParMois) {
    return <div className="loading">Chargement des graphiques...</div>;
  }

  // Couleurs des axes / légendes adaptées au thème actif
  const isDark = theme === 'dark';
  const axisColor = isDark ? '#94a3b8' : '#475569';
  const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.07)';

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        labels: {
          color: axisColor,
          font: { family: 'Inter', size: 12 }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: axisColor,
          callback: (value) => value + ' €'
        },
        grid: { color: gridColor }
      },
      x: {
        ticks: { color: axisColor },
        grid: { color: gridColor }
      }
    }
  };

  const caMoisData = {
    labels: kpi.caParMois.map(item => item.mois),
    datasets: [
      {
        label: 'CA facturé HT',
        data: kpi.caParMois.map(item => item.ca),
        backgroundColor: 'rgba(237, 137, 54, 0.6)',
        borderColor: '#ed8936',
        borderWidth: 2,
        borderRadius: 4,
      },
      {
        label: 'Coûts fournisseurs HT',
        data: (kpi.coutsParMois || []).map(item => item.couts),
        backgroundColor: 'rgba(99, 102, 241, 0.6)',
        borderColor: '#6366f1',
        borderWidth: 2,
        borderRadius: 4,
      }
    ]
  };

  const tvaMoisData = {
    labels: (kpi.tvaParMois || []).map(item => item.mois),
    datasets: [
      {
        label: 'TVA collectée (factures)',
        data: (kpi.tvaParMois || []).map(item => item.collectee),
        backgroundColor: 'rgba(237, 137, 54, 0.6)',
        borderColor: '#ed8936',
        borderWidth: 2,
        borderRadius: 4,
      },
      {
        label: 'TVA déductible (achats)',
        data: (kpi.tvaParMois || []).map(item => item.deductible),
        backgroundColor: 'rgba(99, 102, 241, 0.6)',
        borderColor: '#6366f1',
        borderWidth: 2,
        borderRadius: 4,
      }
    ]
  };

  const top5Chantiers = kpi.topChantiers?.slice(0, 5) || [];
  const chantiersData = {
    labels: top5Chantiers.map(item => item.chantier),
    datasets: [
      {
        label: 'CA facturé HT',
        data: top5Chantiers.map(item => item.ca),
        backgroundColor: 'rgba(237, 137, 54, 0.6)',
        borderColor: '#ed8936',
        borderWidth: 2,
        borderRadius: 4,
      }
    ]
  };

  const top5Clients = kpi.topClients?.slice(0, 5) || [];
  const clientsData = {
    labels: top5Clients.map(item => item.client),
    datasets: [
      {
        label: 'CA facturé HT',
        data: top5Clients.map(item => item.ca),
        backgroundColor: 'rgba(237, 137, 54, 0.6)',
        borderColor: '#ed8936',
        borderWidth: 2,
        borderRadius: 4,
      }
    ]
  };

  const statutsData = {
    labels: kpi.devisParStatut?.map(item => item.statut) || [],
    datasets: [
      {
        data: kpi.devisParStatut?.map(item => item.nb) || [],
        backgroundColor: [
          'rgba(245, 158, 11, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(239, 68, 68, 0.8)'
        ],
        borderColor: [
          '#f59e0b',
          '#10b981',
          '#ef4444'
        ],
        borderWidth: 2,
      }
    ]
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: axisColor,
          font: { family: 'Inter', size: 12 },
          padding: 16,
        }
      }
    }
  };

  return (
    <div className="charts-container">
      <div className="chart-grid">
        <div className="chart-card chart-full">
          <h4>📈 CA facturé & coûts par mois</h4>
          <Bar data={caMoisData} options={chartOptions} />
        </div>
        
        <div className="chart-card chart-full">
          <h4>🧾 TVA collectée & déductible par mois</h4>
          <Bar data={tvaMoisData} options={chartOptions} />
        </div>

        <div className="chart-card">
          <h4>🏆 Top 5 chantiers</h4>
          <Bar 
            data={chantiersData} 
            options={{
              ...chartOptions,
              plugins: { legend: { display: false } }
            }} 
          />
        </div>
        
        <div className="chart-card">
          <h4>🏆 Top 5 clients</h4>
          <Bar 
            data={clientsData} 
            options={{
              ...chartOptions,
              plugins: { legend: { display: false } }
            }} 
          />
        </div>
        
        <div className="chart-card">
          <h4>📊 Statuts des devis</h4>
          <Pie data={statutsData} options={pieOptions} />
        </div>

        <div className="chart-card">
          <h4>📊 Indicateurs clés</h4>
          <div className="gauge-grid">
            <div className="gauge-item">
              <label>Taux d'acceptation</label>
              <div className="gauge-bar">
                <div 
                  className="gauge-fill" 
                  style={{ 
                    width: `${Math.min(kpi.tauxAcceptation || 0, 100)}%`,
                    background: `linear-gradient(90deg, #ed8936, ${(kpi.tauxAcceptation || 0) > 70 ? '#10b981' : '#f59e0b'})`
                  }}
                />
              </div>
              <span>{(kpi.tauxAcceptation || 0).toFixed(1)}%</span>
            </div>
            
            <div className="gauge-item">
              <label>Marge brute</label>
              <div className="gauge-bar">
                <div 
                  className="gauge-fill" 
                  style={{ 
                    width: `${Math.min(Math.max(kpi.margeBrutePct || 0, 0), 100)}%`,
                    background: `linear-gradient(90deg, #ed8936, ${(kpi.margeBrutePct || 0) > 30 ? '#10b981' : '#ef4444'})`
                  }}
                />
              </div>
              <span>{(kpi.margeBrutePct || 0).toFixed(1)}%</span>
            </div>
          </div>
          
          <div className="kpi-mini">
            <div className="kpi-mini-item">
              <span>CA facturé</span>
              <strong>{(kpi.caFactureHT || 0).toFixed(0)} €</strong>
            </div>
            <div className="kpi-mini-item">
              <span>Coûts</span>
              <strong>{(kpi.totalCoutsHT || 0).toFixed(0)} €</strong>
            </div>
            <div className="kpi-mini-item">
              <span>Marge</span>
              <strong>{(kpi.margeBrute || 0).toFixed(0)} €</strong>
            </div>
            <div className="kpi-mini-item">
              <span>Reste à encaisser</span>
              <strong>{(kpi.resteAEncaisser || 0).toFixed(0)} €</strong>
            </div>
            <div className="kpi-mini-item">
              <span>Coûts payés (avance)</span>
              <strong>{(kpi.coutsPayesTTC || 0).toFixed(0)} €</strong>
            </div>
            <div className="kpi-mini-item">
              <span>TVA nette (col. − déd.)</span>
              <strong>{(kpi.tvaNette || 0).toFixed(0)} €</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardCharts;